import {
  OnboardingShell,
  ExpiredLink,
} from "@app/components/onboarding-dialog";
import { collectProviderSession } from "@app/components/oauth-buttons";
import { setAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { onboardingPage_LeadQuery } from "@app/queries/onboardingPage_LeadQuery.graphql";
import type { onboardingPage_LeadStartMutation } from "@app/queries/onboardingPage_LeadStartMutation.graphql";
import type { onboardingPage_LeadAnswerSaveMutation } from "@app/queries/onboardingPage_LeadAnswerSaveMutation.graphql";
import type { onboardingPage_MagicLinkVerifyMutation } from "@app/queries/onboardingPage_MagicLinkVerifyMutation.graphql";

import FunnelView from "./funnel-view";
import { LoadingScreen } from "./screen";
import { forgetAttribution, storedAttribution } from "./attribution";
import { STEP, STEPS, nextStep } from "./funnel";
import { forgetLead, leadToken, rememberLead } from "./lead-token";

import type { SavedAnswer } from "./funnel";
import type { Lead } from "./types";

// Everything the funnel reads. One object, because there is one table behind it:
// the flow itself is funnel.ts and never leaves the client.
//
// `me` rides along so the boot below can tell a visitor from someone who
// already has an account. It is null for anyone without a session, which is
// most of the people this page is for.
const leadQuery = graphql`
  query onboardingPage_LeadQuery($token: String) {
    me {
      accessGrantedAt
    }
    lead(token: $token) {
      token
      firstName
      lastName
      email
      status
      currentStepKey
      converted
      completenessScore
      position
      waitDays
      referralCode
      answers {
        key
        values
        fields {
          key
          value
        }
      }
      uploadedFiles {
        id
        filename
        sourceId
        byteSize
        txnCount
        coverageStart
        coverageEnd
        parseState
        errorMessage
      }
      integrations {
        provider
        available
        connected
        displayName
        connectUrl
      }
    }
  }
`;

const leadStartMutation = graphql`
  mutation onboardingPage_LeadStartMutation(
    $firstName: String!
    $lastName: String!
    $nextStepKey: String
    $attribution: LeadAttributionInputObject
  ) {
    leadStart(
      firstName: $firstName
      lastName: $lastName
      nextStepKey: $nextStepKey
      attribution: $attribution
    ) {
      lead {
        token
      }
      errors {
        message
      }
    }
  }
`;

const answerSaveMutation = graphql`
  mutation onboardingPage_LeadAnswerSaveMutation(
    $token: String
    $stepKey: String!
    $values: [String!]
    $fields: [OnboardingAnswerFieldInputObject!]
    $nextStepKey: String
  ) {
    leadAnswerSave(
      token: $token
      stepKey: $stepKey
      values: $values
      fields: $fields
      nextStepKey: $nextStepKey
    ) {
      lead {
        completenessScore
      }
      errors {
        message
      }
    }
  }
`;

const magicLinkVerifyMutation = graphql`
  mutation onboardingPage_MagicLinkVerifyMutation($token: String!) {
    magicLinkVerify(token: $token) {
      token
      errors {
        message
      }
    }
  }
`;

type Phase = "verifying" | "loading" | "ready" | "invalid";

// The funnel engine. It walks funnel.ts, renders whichever screen the current
// step names, and writes every answer to one row as it is given — the same row
// before the account card and after it, which is the whole point of the shape.
export default function OnboardingPage({
  token,
  providerDone,
  oauthError,
}: {
  token?: string;
  providerDone?: boolean;
  oauthError?: string;
}) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>(
    token || providerDone ? "verifying" : "loading"
  );
  const [lead, setLead] = useState<Lead | null>(null);
  const [answers, setAnswers] = useState<readonly SavedAnswer[]>([]);
  const [stepKey, setStepKey] = useState<string>(STEPS[0].key);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Held past the URL it arrived on, which boot clears.
  const [providerError, setProviderError] = useState(oauthError ?? null);

  const verifyRef = useRef<Promise<string | null> | null>(null);
  const tokenRef = useRef<string | null>(null);
  // Answers are authoritative HERE, not on the mutation's reply. A save is fired
  // and not awaited, so a reply landing after the next question was answered
  // would put the older bag back on screen. The row gets the same writes in the
  // same order; only a deliberate reload reads it back.
  const answersRef = useRef<readonly SavedAnswer[]>([]);

  // Where they have been. A stack rather than a rule about which step precedes
  // which, because the walk forks — and the way back out of a fork is the way
  // you came into it, which only the trail knows.
  const trailRef = useRef<string[]>([]);
  const [depth, setDepth] = useState(0);

  const applyLead = useCallback((fresh: Lead | null) => {
    setLead(fresh);
    if (!fresh) return;
    tokenRef.current = fresh.token;
    answersRef.current = fresh.answers.map((a) => ({
      key: a.key,
      values: [...a.values],
      fields: a.fields.map((f) => ({ key: f.key, value: f.value })),
    }));
    setAnswers(answersRef.current);
  }, []);

  // Returns the whole response rather than just the lead: boot needs `me` too,
  // and a second round trip to ask who is signed in would be one more thing to
  // wait for before the first question can be drawn.
  const load = useCallback(async () => {
    const result = await fetchQuery<onboardingPage_LeadQuery>(
      environment,
      leadQuery,
      { token: tokenRef.current ?? leadToken() },
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .catch(() => null);

    applyLead(result?.lead ?? null);
    return result ?? null;
  }, [applyLead]);

  // Bootstrap. A magic-link token is redeemed first (that's how someone resumes
  // from another device, where the browser holds no token of its own), and a
  // Google or Microsoft sign-in's session is collected from this browser's
  // cookie; otherwise we read back whatever the stored token or the current
  // session can see.
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (token) {
        // The redemption is remembered as a PROMISE rather than a "done" flag. A
        // magic link is single-use, so it must go out once — but this effect
        // remounts (React does it on every mount in development), and a flag
        // makes the second run skip this branch entirely and render the funnel
        // over a link the server actually rejected. Awaiting the same promise
        // redeems once and still handles the answer both times.
        verifyRef.current ??= new Promise<string | null>((resolve) => {
          commitMutation<onboardingPage_MagicLinkVerifyMutation>(environment, {
            mutation: magicLinkVerifyMutation,
            variables: { token },
            onCompleted: (response) =>
              resolve(response.magicLinkVerify?.token ?? null),
            onError: () => resolve(null),
          });
        });
        const jwt = await verifyRef.current;

        if (cancelled) return;
        if (!jwt) {
          setPhase("invalid");
          return;
        }
        setAuthToken(jwt);
        // Drop the token from the URL so a refresh doesn't re-redeem it.
        navigate({ to: "/onboarding", search: {}, replace: true });
      }

      // Back from Google or Microsoft. Same promise-in-a-ref as above: the
      // server hands the session over once. If it isn't there — collected, or
      // left over a minute — the card says so rather than the funnel pretending.
      let providerFailed = false;
      if (providerDone) {
        verifyRef.current ??= collectProviderSession().catch(() => null);
        const jwt = await verifyRef.current;

        if (cancelled) return;
        if (jwt) setAuthToken(jwt);
        else {
          providerFailed = true;
          setProviderError("FAILED");
        }
        navigate({ to: "/onboarding", search: {}, replace: true });
      }

      // A provider sign-in that didn't take comes back saying why. It is said on
      // the account card it was started from, whatever step the row is parked
      // on, and dropped from the URL so a reload doesn't say it again.
      if (oauthError)
        navigate({ to: "/onboarding", search: {}, replace: true });

      const data = await load();
      if (cancelled) return;
      const fresh = data?.lead ?? null;

      // Someone who is already signed in does not belong at the top of the
      // funnel. Two of them do not belong in it at all: an existing account
      // with no run behind it, and one that has already been let in. Both used
      // to be walked back through the questions from "what's your name", and
      // the first of them could not answer them — with a session but no run,
      // every write after the name came back "We couldn't find that session."
      //
      // Anyone else signed in has a run still in flight, so they stay and
      // resume on the step they left, waiting-list screen included.
      if (data?.me && (!fresh || data.me.accessGrantedAt)) {
        void navigate({ to: "/dashboard", replace: true });
        return;
      }

      // A spent token: the row converted, or it is gone. Either way the browser
      // has no business holding it.
      if (!fresh && leadToken()) forgetLead();

      setStepKey(
        oauthError || providerFailed
          ? STEP.EMAIL
          : fresh?.currentStepKey ?? STEPS[0].key
      );
      setPhase("ready");
    };

    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const walkedTo = useCallback((from: string) => {
    trailRef.current.push(from);
    setDepth(trailRef.current.length);
  }, []);

  const back = useCallback(() => {
    const previous = trailRef.current.pop();
    setDepth(trailRef.current.length);
    if (previous) setStepKey(previous);
  }, []);

  // The name question opens the row; every question after it patches the same
  // one. `nextStepKey` is where the CLIENT walked, because the walk forks and
  // only the client knows which way a lead went.
  //
  // Declared before `save` because `save` depends on it, and a const referenced
  // in a dependency array before its own declaration is a temporal-dead-zone
  // crash waiting for the first re-render.
  const record = useCallback(
    async (
      key: string,
      input: { values?: string[]; fields?: { key: string; value: string }[] },
      next: string | null
    ) => {
      if (!tokenRef.current) {
        const first = input.fields?.find((f) => f.key === "first_name")?.value;
        const last = input.fields?.find((f) => f.key === "last_name")?.value;
        // Any step arriving before the name has nothing to open a row with. It
        // cannot happen on the walk — the greeting is the first screen — so
        // this is a client that got ahead of itself, and dropping the write is
        // better than opening a nameless row for it.
        if (!first || !last) return;

        const minted = await new Promise<string | null>((resolve) => {
          commitMutation<onboardingPage_LeadStartMutation>(environment, {
            mutation: leadStartMutation,
            variables: {
              firstName: first,
              lastName: last,
              nextStepKey: next,
              attribution: storedAttribution(),
            },
            onCompleted: (response) => {
              const message = response.leadStart?.errors?.[0]?.message;
              // A refused name opens no row, so nothing after it can be saved:
              // walk them back to it rather than on through a funnel that
              // writes nowhere and ends in an account with no answers.
              if (message) {
                setSaveError(message);
                trailRef.current = [];
                setDepth(0);
                setStepKey(STEP.NAME);
              }
              resolve(response.leadStart?.lead?.token ?? null);
            },
            onError: () => resolve(null),
          });
        });

        if (!minted) return;
        tokenRef.current = minted;
        rememberLead(minted);
        forgetAttribution();
        return;
      }

      await new Promise<void>((resolve) => {
        commitMutation<onboardingPage_LeadAnswerSaveMutation>(environment, {
          mutation: answerSaveMutation,
          variables: {
            token: tokenRef.current,
            stepKey: key,
            values: input.values ?? [],
            fields: input.fields ?? [],
            nextStepKey: next,
          },
          onCompleted: (response) => {
            const message = response.leadAnswerSave?.errors?.[0]?.message;
            if (message) setSaveError(message);
            resolve();
          },
          onError: () => resolve(),
        });
      });
    },
    []
  );

  // One answer, written down. The screen moves on the optimistic copy — a
  // question should never wait on a round trip to be answered — and the row
  // catches up behind it.
  const save = useCallback(
    (
      key: string,
      input: { values?: string[]; fields?: { key: string; value: string }[] }
    ) => {
      const values = input.values ?? [];
      const fields = input.fields ?? [];
      const held: SavedAnswer = { key, values, fields };

      answersRef.current = [
        ...answersRef.current.filter((a) => a.key !== key),
        held,
      ];
      setAnswers(answersRef.current);
      setSaveError(null);

      // The hub is not left by answering it: a lead resolves one source at a
      // time and stays put, and finalizing is what ends the run.
      const next =
        key === STEP.DATA_HUB
          ? null
          : nextStep(key, values, answersRef.current);
      if (next) {
        walkedTo(key);
        setStepKey(next);
      }

      void record(key, input, next);
    },
    [walkedTo, record]
  );

  // The account card mints the session itself, so the funnel reloads behind it:
  // the row it reads back is the same one, now with a user attached and the
  // integrations that come with an account.
  const onConverted = useCallback(async () => {
    forgetLead();
    setBusy(true);
    const data = await load();
    setBusy(false);
    walkedTo(STEP.EMAIL);
    setStepKey(data?.lead?.currentStepKey ?? STEP.DATA_HUB);
  }, [load, walkedTo]);

  if (phase === "invalid") {
    return (
      <OnboardingShell>
        <ExpiredLink />
      </OnboardingShell>
    );
  }

  if (phase !== "ready") return <LoadingScreen />;

  return (
    <FunnelView
      lead={lead}
      answers={answers}
      stepKey={stepKey}
      busy={busy}
      saveError={saveError}
      token={tokenRef.current}
      save={save}
      back={depth > 0 ? back : undefined}
      onConverted={onConverted}
      reload={async () => void (await load())}
      oauthError={providerError}
    />
  );
}
